import { zodResolver } from '@hookform/resolvers/zod'
import type { BackofficeAuthVerifyTotpData } from '@repo/sdk/gen'
import { backofficeAuthVerifyTotp } from '@repo/sdk/gen'
import { Button } from '@repo/ui/button'
import { ButtonLink } from '@repo/ui/commons/button-link'
import { Field, FieldError, FieldGroup } from '@repo/ui/field'
import {
	AuthCard,
	AuthCardBody,
	AuthCardHeader
} from '@repo/ui/modules/auth/components/auth-card'
import { AuthErrorAlert } from '@repo/ui/modules/auth/components/auth-error-alert'
import { OtpInput } from '@repo/ui/modules/auth/components/otp-input'
import { TwoFactorDevicePicker } from '@repo/ui/modules/auth/components/two-factor-device-picker'
import { Spinner } from '@repo/ui/spinner'
import { formatError } from '@repo/ui/utils'
import {
	useMutation,
	useQueryClient,
	useSuspenseQuery
} from '@tanstack/react-query'
import {
	createFileRoute,
	redirect,
	useNavigate,
	useRouter
} from '@tanstack/react-router'
import { Smartphone } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import i18n, { useTranslation } from '@/lib/i18n'
import {
	adminSessionQueryOptions,
	fetchFreshSession
} from '@/lib/query-options'

const TotpSchema = z.object({
	code: z
		.string()
		.length(6, { error: () => i18n.t('auth:validation.otp_length') })
})

export const Route = createFileRoute('/(auth)/two-factor/')({
	// The default challenge step is the authenticator; without an enrolled
	// device the choice screen is the only sensible landing.
	beforeLoad: async ({ context, search }) => {
		const session = await context.queryClient.fetchQuery(
			adminSessionQueryOptions()
		)
		if (
			session.sessionType === 'mfa_pending' &&
			!session.mfa?.methods.some((method) => method.type === 'totp')
		) {
			throw redirect({ to: '/two-factor/methods', search })
		}
	},
	component: AdminTwoFactorTotp
})

function AdminTwoFactorTotp() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const router = useRouter()
	const queryClient = useQueryClient()
	const { redirect: redirectTo } = Route.useSearch()

	// The layout guard ensured a pending challenge; the null case only occurs
	// for an instant after a successful verify, while the navigation away is
	// still in flight.
	const { data: session } = useSuspenseQuery(adminSessionQueryOptions())
	const mfa = session.sessionType === 'mfa_pending' ? session.mfa : null

	const devices =
		mfa?.methods.filter((method) => method.type === 'totp') ?? []
	const [deviceId, setDeviceId] = useState(devices[0]?.deviceId ?? '')

	const form = useForm<z.infer<typeof TotpSchema>>({
		resolver: zodResolver(TotpSchema),
		defaultValues: { code: '' }
	})

	const verify = useMutation({
		mutationFn: (body: BackofficeAuthVerifyTotpData['body']) =>
			backofficeAuthVerifyTotp({ body }),
		onError: () => form.resetField('code'),
		onSuccess: async () => {
			await fetchFreshSession(queryClient)
			// The dashboard loads before the navigation, so it opens ready
			// instead of flashing its loader. A failed preload only costs
			// that: navigation loads the route again.
			await router
				.preloadRoute({ to: redirectTo ?? '/' })
				.catch(() => undefined)
			await navigate({ to: redirectTo ?? '/' })
		}
	})

	if (!mfa) {
		return null
	}

	return (
		<AuthCard>
			<AuthCardHeader
				description={t('auth:two_factor.totp_description')}
				icon={<Smartphone className='size-8' />}
				title={t('auth:two_factor.title')}
			/>
			<AuthCardBody>
				<form
					onSubmit={form.handleSubmit((data) =>
						verify.mutate({
							mfaToken: mfa.mfaToken,
							deviceId,
							code: data.code
						})
					)}
				>
					<FieldGroup>
						{verify.isError && (
							<AuthErrorAlert
								onClose={() => verify.reset()}
								title={formatError(verify.error)}
							/>
						)}
						{devices.length > 1 && (
							<TwoFactorDevicePicker
								devices={devices}
								onChange={setDeviceId}
								value={deviceId}
							/>
						)}
						<Controller
							control={form.control}
							name='code'
							render={({ field, fieldState }) => (
								<Field data-invalid={fieldState.invalid}>
									<OtpInput
										disabled={verify.isPending}
										onChange={field.onChange}
										value={field.value}
									/>
									{fieldState.invalid && (
										<FieldError
											errors={[fieldState.error]}
										/>
									)}
								</Field>
							)}
						/>
						<Field>
							<Button disabled={verify.isPending} type='submit'>
								{verify.isPending ? (
									<Spinner />
								) : (
									t('auth:two_factor.verify')
								)}
							</Button>
						</Field>
					</FieldGroup>
				</form>
				<div className='flex justify-center'>
					<ButtonLink
						search={(previous) => previous}
						to='/two-factor/methods'
						variant='link'
					>
						{t('auth:two_factor.use_another_method')}
					</ButtonLink>
				</div>
			</AuthCardBody>
		</AuthCard>
	)
}
